"use client";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext"; // Import AuthContext
import {
  LayoutDashboard,
  Users,
  Calendar,
  CalendarOff,
  Clock,
  Wallet,
  TrendingUp,
  Gift,
  UserPlus,
  AlertTriangle,
  Building,
  FileType,
  Briefcase,
  History,
  Receipt,
  User,
  Settings,
  LogOut,
  ChevronDown,
  ChevronUp,
  UserCheck,
  Building2,
  Award,
  CalendarDays,
  Tags,
  Fingerprint,
  FileBarChart2,
  FileSpreadsheet,
  FolderOpen,
  Layers,
  ShieldCheck,
  ListChecks,
  ClipboardList,
  Bug,
  UsersRound,
  Gauge,
  BarChart3,
  LineChart,
  FileText,
  Megaphone,
  FileSignature,
  Package,
  Boxes,
  PackageCheck,
  Wrench,
  CalendarRange,
  GitBranch,
} from "lucide-react";

const navigationItems = [
  { icon: LayoutDashboard, label: "Dashboard", route: "/dashboard", type: "single" },
  { icon: GitBranch, label: "Organization Chart", route: "/OrganizationChart", type: "single" },
  {
    icon: Users,
    label: "Employee",
    type: "dropdown",
    children: [
      { icon: UserCheck, label: "Employees", route: "/Employee" },
      { icon: Building2, label: "Departments", route: "/Departments" },
      { icon: Award, label: "Designations", route: "/Designations" },
    ],
  },
  {
    icon: Clock,
    label: "Attendance",
    type: "dropdown",
    children: [
      { icon: Fingerprint, label: "Attendance", route: "/Attendence" },
      { icon: FileBarChart2, label: "Attendence Report", route: "/AttendenceReport" },
      { icon: FileSpreadsheet, label: "Timesheet", route: "/TimeSheet" },
    ],
  },
  {
    icon: Calendar,
    label: "Leaves",
    type: "dropdown",
    children: [
      { icon: CalendarDays, label: "Holidays", route: "/Holidays" },
      { icon: Tags, label: "Leave Types", route: "/leaveTypes" },
      { icon: CalendarOff, label: "Leaves", route: "/leaves" },
      // { icon: CalendarRange, label: "Calendar View", route: "/CalendarView" },
    ],
  },
  {
    icon: Award,
    label: "Performance Hub",
    type: "dropdown",
    children: [
      { icon: LineChart, label: "Employee Performance", route: "/Work" },
      { icon: UsersRound, label: "EmployeeWork", route: "/EmployeeWork" },
      { icon: Gauge, label: "Manager Dashboard", route: "/ManagerDashboard" },
    ],
  },
  {
    icon: ListChecks,
    label: "Tasks & Requests",
    type: "dropdown",
    children: [
      { icon: Gauge, label: "Insights Dashboard", route: "/TasksRequestsDashboard", adminHrOnly: true },
      { icon: Bug, label: "Request Management", route: "/RequestManagement", hideOnMobile: true },
      { icon: ClipboardList, label: "Task", route: "/Task", hideOnMobile: true },
    ],
  },
  {
    icon: Briefcase,
    label: "Projects",
    type: "dropdown",
    children: [
      { icon: FolderOpen, label: "Projects", route: "/Project" },
      { icon: Layers, label: "Project Types", route: "/ProjectTypes" },
      { icon: BarChart3, label: "Project Insights", route: "/ProjectInsights" },
    ],
  },
  { icon: TrendingUp, label: "Performance", route: "/performance", type: "single", hideOnMobile: true },
  {
    icon: Wallet,
    label: "Payroll",
    type: "dropdown",
    children: [
      { icon: Gift, label: "Bonus Types", route: "/BonusTypes" },
      { icon: Wallet, label: "Payroll", route: "/payrolls" },
    ],
  },
  { icon: Building, label: "Clients", route: "/client", type: "single", hideOnMobile: true },
  { icon: ShieldCheck, label: "Roles", route: "/roles", type: "single", hideOnMobile: true },
  {
    icon: AlertTriangle,
    label: "Workplace Issues",
    type: "dropdown",
    children: [
      {
        icon: AlertTriangle,
        label: "Workplace Issues",
        permission: "WorkplaceIssues",
        route: "/Workplacelssues",
      },
      {
        icon: AlertTriangle,
        label: "Workplace Issues Category",
        permission: "WorkplaceIssuesCategory",
        route: "/WorkplaceIssuesCategory",
      },
    ],
    hideOnMobile: true,
  },
  {
    icon: FileText,
    label: "Policies",
    type: "dropdown",
    hideOnMobile: true,
    children: [
      {
        icon: FileText,
        label: "Policy Category",
        route: "/PolicyCategory",
        alwaysVisible: true,
      },
      {
        icon: FileText,
        label: "Policy Management",
        route: "/PolicyManagement",
        alwaysVisible: true,
      },
    ],
  },
  { icon: Megaphone, label: "Announcements", route: "/AnnouncementManagement", type: "single", alwaysVisible: true },
  {
    icon: FileSignature,
    label: "Letters",
    type: "dropdown",
    alwaysVisible: true,
    children: [
      { icon: FileType, label: "Document Types", route: "/DocumentTypes" },
      { icon: FileSignature, label: "Letters", route: "/letters" },
    ],
  },
  {
    icon: Award,
    label: "Certificates",
    route: "/certificates",
    type: "single",
    alwaysVisible: true,
  },
  {
    icon: Package,
    label: "Asset Management",
    type: "dropdown",
    children: [
      { icon: Boxes, label: "Asset Categories", route: "/CategoryType" },
      { icon: PackageCheck, label: "Asset Types", route: "/AssetType" },
      { icon: Wrench, label: "Assets Management", route: "/AssetsManagement" },
    ],
  },
  { icon: UserCheck, label: "Resume Management", route: "/Recruitmentmanagement", type: "single" },
  { icon: User, label: "User Profile", route: "/profile", type: "single" },
  { icon: History, label: "Audit Logs", route: "/audit-logs", type: "single" },
  { icon: Settings, label: "Settings", route: "/settings", type: "single" },
];

// Simple skeleton shown while permissions are still loading, so the sidebar
// never flashes a partial (permission-less) menu before the real one arrives.
function SidebarSkeleton({ collapsed }) {
  const rows = Array.from({ length: 10 });
  return (
    <ul className="list-none p-0 m-0" aria-hidden="true">
      {rows.map((_, i) => (
        <li key={`skeleton-${i}`} className="mb-1">
          <div
            className={`animate-pulse flex items-center gap-3 py-3 rounded-lg bg-white bg-opacity-10 ${
              collapsed ? "px-2 justify-center" : "px-4"
            }`}
          >
            <div className="w-5 h-5 rounded bg-white bg-opacity-20 flex-shrink-0" />
            {!collapsed && (
              <div
                className="h-3 rounded bg-white bg-opacity-20"
                style={{ width: `${50 + ((i * 13) % 40)}%` }}
              />
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function Sidebar({ collapsed, visible, isMobile, isTablet, onToggle, onClose }) {
  const { user, permissionsLoading } = useAuth();
  const permissions = user?.permissions || [];
  const userRole = (user?.role || "").toLowerCase();
  const isAdminOrHr = userRole === "admin" || userRole === "hr";
  const navigate = useNavigate();
  const location = useLocation();

  const [activeItem, setActiveItem] = useState("Dashboard");
  const [openDropdowns, setOpenDropdowns] = useState({});

  // Treat tablets like mobile devices - no collapsed state
  const isMobileOrTablet = isMobile || isTablet;

  // Memoized filtered navigation based on permissions and device type
  const filteredNavigationItems = useMemo(() => {
    return navigationItems
      .map((item) => {
        // Hide items flagged as mobile-only-hidden when on a mobile viewport
        if (isMobile && item.hideOnMobile) return null;

        if (item.type === "single") {
          if (item.alwaysVisible) return item;
          return permissions.includes(item.label) ||
            permissions.includes(item.route.replace("/", "")) ||
            permissions.includes(item.label.replace(/\s/g, ""))
            ? item
            : null;
        }
        if (item.type === "dropdown") {
          const childrenWithPermission = item.children.filter((child) => {
            // Hide individual dropdown children flagged as mobile-only-hidden
            if (isMobile && child.hideOnMobile) return false;

            if (child.alwaysVisible) return true;

            return child.adminHrOnly
              ? isAdminOrHr
              : permissions.includes(child.label) ||
                  permissions.includes(child.route.replace("/", "")) ||
                  permissions.includes(child.label.replace(/\s/g, ""));
          });
          return childrenWithPermission.length > 0 ? { ...item, children: childrenWithPermission } : null;
        }
        return null;
      })
      .filter(Boolean);
  }, [permissions, isAdminOrHr, isMobile]);

  // Memoized route map
  const routeMap = useMemo(() => {
    const map = {};
    filteredNavigationItems.forEach((item) => {
      if (item.type === "single") map[item.route] = item.label;
      if (item.type === "dropdown") {
        item.children.forEach((child) => {
          map[child.route] = child.label;
        });
      }
    });
    return map;
  }, [filteredNavigationItems]);

  const findParentItem = (childLabel) => {
    for (const item of filteredNavigationItems) {
      if (item.children?.some((child) => child.label === childLabel)) return item.label;
    }
    return null;
  };

  // Update active item on route change
  useEffect(() => {
    const currentActiveItem = routeMap[location.pathname] || "Dashboard";
    setActiveItem(currentActiveItem);

    const parentItem = findParentItem(currentActiveItem);
    if (parentItem) {
      setOpenDropdowns((prev) => ({ ...prev, [parentItem]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, routeMap]);

  const toggleDropdown = (label) => {
    // For desktop only when not collapsed, for mobile/tablet always allow
    if (!isMobileOrTablet && collapsed) return;
    setOpenDropdowns((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleNavigation = (route, label) => {
    setActiveItem(label);
    if (route) {
      navigate(route);
      // Close sidebar on mobile and tablet after navigation
      if (isMobileOrTablet && onClose) {
        onClose();
      }
    }
  };

  const handleDropdownParentClick = (item) => {
    // For desktop: if collapsed, navigate to first child; otherwise toggle dropdown
    // For mobile/tablet: always toggle dropdown (no collapsed state)
    if (!isMobileOrTablet && collapsed && item.children?.length > 0) {
      handleNavigation(item.children[0].route, item.children[0].label);
    } else {
      toggleDropdown(item.label);
    }
  };

  const handleChildItemClick = (child, parentLabel) => {
    handleNavigation(child.route, child.label);
    setOpenDropdowns((prev) => ({ ...prev, [parentLabel]: true }));
  };

  const isChildActive = (item) => item.children?.some((child) => child.label === activeItem);
  const isParentActive = (item) => activeItem === item.label || isChildActive(item);

  const getSidebarClasses = () => {
    let baseClasses =
      "box-border flex fixed top-0 left-0 flex-col py-5 m-0 h-screen transition-all duration-300 ease-in-out bg-gradient-to-b from-[#2FACDA] via-[#2455A6] to-[#D12465]";

    // Treat both mobile and tablet the same way
    if (isMobileOrTablet) {
      baseClasses += ` w-64 z-[1000] ${visible ? "translate-x-0" : "-translate-x-full"}`;
    } else {
      // Desktop behavior remains the same
      baseClasses += ` z-[1000] ${collapsed ? "w-16" : "w-64"}`;
    }

    return baseClasses;
  };

  // Determine if we should show collapsed view (desktop only)
  const shouldShowCollapsed = !isMobileOrTablet && collapsed;

  return (
    <aside
      className={`${getSidebarClasses()} flex flex-col h-screen overflow-hidden`}
      style={{
        background: "linear-gradient(180deg, #1e293b 0%, #334155 26%, #2455A6 58%, #D12465 100%)",
        borderTopRightRadius: collapsed && !isMobile ? "18px" : "26px",
        borderBottomRightRadius: collapsed && !isMobile ? "18px" : "26px",
      }}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Header */}
      <header className={`pt-0 pb-8 m-0 ${shouldShowCollapsed ? "px-2" : "px-5"}`}>
        <div className="flex gap-3 items-center">
          <div className="w-10 h-10 bg-white rounded-full overflow-hidden flex items-center justify-center">
            <img src="https://cdn.acsdev.in/fynryx/acs.png" alt="Logo" className="w-full h-full object-cover" />
          </div>
          {!shouldShowCollapsed && <div className="text-white font-semibold text-lg">HRMS</div>}
        </div>
      </header>

      {/* Navigation */}
      <nav
        className={`flex-1 overflow-y-auto custom-scrollbar ${shouldShowCollapsed ? "px-2" : "px-5"}`}
        role="navigation"
        aria-label="Main navigation"
      >
        {permissionsLoading ? (
          // NEW: while the initial permissions fetch is in flight, show a
          // skeleton instead of the (currently permission-less) filtered menu.
          // Prevents the "few items now, rest pop in later" flash.
          <SidebarSkeleton collapsed={shouldShowCollapsed} />
        ) : (
          <ul className="list-none p-0 m-0" role="menubar">
            {filteredNavigationItems.map((item, index) => {
              const IconComponent = item.icon;
              const isDropdownOpen = openDropdowns[item.label];
              const itemIsActive = isParentActive(item);
              const uniqueKey = `nav-item-${item.label}-${index}`;

              if (item.type === "single") {
                return (
                  <li key={uniqueKey} className="mb-1" role="none">
                    <button
                      onClick={() => handleNavigation(item.route, item.label)}
                      className={`box-border flex gap-3 items-center py-3 m-0 w-full rounded-lg transition-all cursor-pointer duration-[0.2s] ease-[ease] focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                        shouldShowCollapsed ? "px-2 justify-center" : "px-4"
                      } ${
                        activeItem === item.label
                          ? "bg-white text-[#2A3547] shadow-lg"
                          : "text-white text-opacity-80 hover:bg-white hover:bg-opacity-10"
                      }`}
                      aria-label={item.label}
                      title={shouldShowCollapsed ? item.label : undefined}
                      role="menuitem"
                    >
                      <IconComponent size={20} className="flex-shrink-0" aria-hidden="true" />
                      {!shouldShowCollapsed && (
                        <span className="box-border p-0 m-0 text-sm font-medium whitespace-nowrap text-left">
                          {item.label}
                        </span>
                      )}
                    </button>
                  </li>
                );
              }

              return (
                <li key={uniqueKey} className="mb-1" role="none">
                  <div role="none">
                    <button
                      onClick={() => handleDropdownParentClick(item)}
                      className={`box-border flex gap-3 items-center py-3 m-0 w-full rounded-lg transition-all cursor-pointer duration-[0.2s] ease-[ease] focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                        shouldShowCollapsed ? "px-2 justify-center" : "px-4"
                      } ${
                        activeItem === item.label
                          ? "bg-white text-[#2A3547] shadow-lg"
                          : "text-white text-opacity-80 hover:bg-white hover:bg-opacity-10"
                      }`}
                      aria-label={shouldShowCollapsed ? item.label : `${item.label} menu`}
                      aria-expanded={shouldShowCollapsed ? undefined : isDropdownOpen}
                      aria-haspopup="true"
                      title={shouldShowCollapsed ? item.label : undefined}
                      role="menuitem"
                    >
                      <IconComponent size={20} className="flex-shrink-0" aria-hidden="true" />
                      {!shouldShowCollapsed && (
                        <>
                          <span className="box-border p-0 m-0 text-sm font-medium whitespace-nowrap flex-1 text-left">
                            {item.label}
                          </span>
                          {isDropdownOpen ? (
                            <ChevronUp size={16} className="flex-shrink-0" aria-hidden="true" />
                          ) : (
                            <ChevronDown size={16} className="flex-shrink-0" aria-hidden="true" />
                          )}
                        </>
                      )}
                    </button>

                    {!shouldShowCollapsed && isDropdownOpen && item.children && (
                      <ul className="mt-1 ml-4 space-y-1" role="menu" aria-label={`${item.label} submenu`}>
                        {item.children.map((child, childIndex) => {
                          const ChildIconComponent = child.icon;
                          const childKey = `nav-child-${item.label}-${child.label}-${childIndex}`;
                          return (
                            <li key={childKey} role="none">
                              <button
                                onClick={() => handleChildItemClick(child, item.label)}
                                className={`box-border flex gap-3 items-center py-2 px-3 m-0 w-full rounded-md transition-all cursor-pointer duration-[0.2s] ease-[ease] focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                                  activeItem === child.label
                                    ? "bg-white text-[#2A3547] shadow-lg"
                                    : "text-white text-opacity-70 hover:bg-white hover:bg-opacity-10"
                                }`}
                                aria-label={child.label}
                                role="menuitem"
                              >
                                <ChildIconComponent size={16} className="flex-shrink-0" aria-hidden="true" />
                                <span className="box-border p-0 m-0 text-xs font-medium whitespace-nowrap text-left">
                                  {child.label}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      {/* Footer */}
      <footer className={`mt-auto ${shouldShowCollapsed ? "px-2" : "px-5"} py-0 m-0`}>
        <button
          onClick={() => {
            const confirmLogout = window.confirm("Are you sure you want to logout?");
            if (confirmLogout) {
              localStorage.clear();
              navigate("/");
            }
          }}
          className={`flex gap-3 items-center py-3 w-full rounded-lg border border-white border-opacity-20 text-white text-opacity-80 hover:bg-white hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
            shouldShowCollapsed ? "px-2 justify-center" : "px-4"
          }`}
          title={shouldShowCollapsed ? "Logout" : undefined}
          aria-label="Logout"
        >
          <LogOut size={18} />
          {!shouldShowCollapsed && <span className="text-xs font-semibold tracking-wide">LOGOUT</span>}
        </button>
      </footer>
    </aside>
  );
}